import React, { useEffect, useMemo, useState } from "react";
import { Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/zh-tw";
import "moment/locale/zh-cn";
import { uiLocale } from "../../helpers/uiLocale";
import DataTable from "../dataTable";
import { formatAmount } from "./generalLedgerUtils";

const GeneralLedgerView = ({ rows = [], loading = false, i18nLanguage = "en" }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchText, setSearchText] = useState("");

  const columns = useMemo(
    () => [
      {
        field: "entryDate",
        headerName: "Date",
        sortable: true,
        cellRender: (col, data) =>
          data.entryDate
            ? moment(data.entryDate)
                .locale(uiLocale(i18nLanguage))
                .format("LL")
            : "",
      },
      { field: "journalCode", headerName: "Code", sortable: true },
      {
        field: "accountCode",
        headerName: "accounting.journal.account",
        sortable: true,
        cellRender: (col, data) =>
          `${data.accountCode || ""} ${data.accountName || ""}`.trim(),
      },
      { field: "description", headerName: "Description", sortable: true },
      {
        field: "sourceType",
        headerName: "accounting.journal.source",
        sortable: true,
        cellRender: (col, data) =>
          data.sourceType
            ? `${data.sourceType}${data.eventType ? ` / ${data.eventType}` : ""}`
            : "",
      },
      {
        field: "debit",
        headerName: "Debit",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.debit),
      },
      {
        field: "credit",
        headerName: "Credit",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.credit),
      },
      {
        field: "runningBalance",
        headerName: "accounting.financeReports.generalLedger.runningBalance",
        sortable: true,
        cellRender: (col, data) => formatAmount(data.runningBalance),
      },
    ],
    []
  );

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        row.journalCode?.toLowerCase().includes(query) ||
        row.description?.toLowerCase().includes(query) ||
        row.accountCode?.toLowerCase().includes(query) ||
        row.accountName?.toLowerCase().includes(query) ||
        row.sourceType?.toLowerCase().includes(query)
    );
  }, [rows, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, rows.length]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize).map((row, index) => ({
      ...row,
      _id: `${row.journalCode}-${row.accountCode}-${start + index}`,
    }));
  }, [filteredRows, currentPage, pageSize]);

  if (loading && rows.length === 0) {
    return (
      <Text size="sm" color="dimmed">
        {t("accounting.financeReports.generalLedger.loading")}
      </Text>
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <Text size="sm" color="dimmed">
        {t("accounting.financeReports.generalLedger.noActivity")}
      </Text>
    );
  }

  return (
    <DataTable
      data={pagedRows}
      columns={columns}
      hidePagination={filteredRows.length <= pageSize}
      hideSearchBar={false}
      searchText={searchText}
      onSearchChange={setSearchText}
      padding="md"
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => {
        setPageSize(Number(size) || 50);
        setCurrentPage(1);
      }}
      pagination={{
        total: filteredRows.length,
        totalPage: Math.max(1, Math.ceil(filteredRows.length / pageSize)),
        page: currentPage,
      }}
    />
  );
};

export default GeneralLedgerView;
